import { parseDateKey, formatDateOnly } from "@/lib/time";
import { CalendarDays, X } from "lucide-react";
import { useRef } from "react";

/**
 * Styled date picker that works on both mobile and desktop.
 *
 * A styled display div shows the formatted date value.
 * A fully transparent native <input type="date"> sits on top and receives
 * all pointer events. On mobile, tapping the overlay triggers the system
 * picker natively. On desktop, clicking calls showPicker() (which works
 * when triggered by a direct user gesture on the live site).
 *
 * Note: showPicker() will fail silently in cross-origin iframes (Manus preview)
 * but works correctly on the live site.
 */
export function DatePickerInput({
  value,
  onChange,
  onClear,
  style,
  className,
  dateFormat = "long",
  showCalendarIcon = false,
}: {
  value: string | null; // "YYYY-MM-DD" or null
  onChange: (value: string) => void;
  onClear?: () => void;
  style?: React.CSSProperties;
  className?: string;
  dateFormat?: "long" | "short";
  showCalendarIcon?: boolean;
}) {
  const inputRef = useRef<HTMLInputElement>(null);

  const formatDisplay = (v: string | null) => {
    if (!v) return "";
    try {
      const date = parseDateKey(v);
      return dateFormat === "short"
        ? date.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })
        : formatDateOnly(date);
    } catch {
      return v;
    }
  };

  const displayText = formatDisplay(value);

  const handleClick = () => {
    try {
      inputRef.current?.showPicker();
    } catch {
      // showPicker() fails in cross-origin iframes — ignore silently
      // On mobile the transparent overlay handles it natively
    }
  };

  return (
    <div
      className={`date-wrapper relative w-full cursor-pointer ${className ?? ""}`}
      style={style}
      onClick={handleClick}
    >
      {/* Styled display list — shows formatted date, pointer-events-none */}
      <div className={`w-full px-3 py-2 pr-9 border border-input rounded-md bg-background text-foreground text-sm text-left select-none pointer-events-none min-h-[38px]${showCalendarIcon ? " flex items-center gap-2" : ""}`}>
        {showCalendarIcon && <CalendarDays className="h-4 w-4 shrink-0" aria-hidden="true" />}
        <span className="min-w-0 truncate">{displayText || <span className="text-muted-foreground">--- --, ---- </span>}</span>
      </div>

      {/* Transparent native input — receives all pointer events */}
      <input
        ref={inputRef}
        type="date"
        value={value ?? ""}
        onChange={e => onChange(e.target.value || "")}
        className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
      />
      {value && onClear && (
        <button
          type="button"
          className="absolute right-2 top-1/2 z-10 inline-flex h-6 w-6 -translate-y-1/2 items-center justify-center rounded-full text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
          onMouseDown={e => e.preventDefault()}
          onClick={e => {
            e.stopPropagation();
            onClear();
          }}
          aria-label="Clear date"
        >
          <X className="h-3.5 w-3.5" />
        </button>
      )}
    </div>
  );
}
